export type ScheduleType = 'manual' | 'hourly' | 'every_6h' | 'daily' | 'weekly';

const SCHEDULE_OPTIONS: { value: ScheduleType; label: string; description: string }[] = [
  { value: 'manual', label: 'Manual', description: 'Run manually via button or API' },
  { value: 'hourly', label: 'Hourly', description: 'Every hour' },
  { value: 'every_6h', label: 'Every 6 Hours', description: '4 times per day' },
  { value: 'daily', label: 'Daily', description: 'Once per day at a specific hour' },
  { value: 'weekly', label: 'Weekly', description: 'Once per week on a specific day and hour' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')}:00 UTC`,
}));

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

interface SchedulePickerProps {
  schedule: ScheduleType;
  hour: number;
  dayOfWeek: number;
  onScheduleChange: (schedule: ScheduleType) => void;
  onHourChange: (hour: number) => void;
  onDayOfWeekChange: (day: number) => void;
}

export function SchedulePicker({
  schedule,
  hour,
  dayOfWeek,
  onScheduleChange,
  onHourChange,
  onDayOfWeekChange,
}: SchedulePickerProps) {
  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {SCHEDULE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onScheduleChange(opt.value)}
            className={`rounded-lg border p-3 text-left transition-colors ${
              schedule === opt.value
                ? 'border-brand-300 bg-brand-50 ring-1 ring-brand-300'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <p className={`text-sm font-medium ${schedule === opt.value ? 'text-brand-700' : 'text-gray-900'}`}>
              {opt.label}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">{opt.description}</p>
          </button>
        ))}
      </div>

      {(schedule === 'daily' || schedule === 'weekly') && (
        <div className="flex flex-wrap gap-4">
          {schedule === 'weekly' && (
            <div>
              <label className="block text-xs font-medium text-gray-700">Day of Week</label>
              <select
                value={dayOfWeek}
                onChange={(e) => onDayOfWeekChange(Number(e.target.value))}
                className="mt-1 block rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700">Hour</label>
            <select
              value={hour}
              onChange={(e) => onHourChange(Number(e.target.value))}
              className="mt-1 block rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              {HOURS.map((h) => (
                <option key={h.value} value={h.value}>
                  {h.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
